import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  consultationRecordingsAPI,
  type ConsultationRecordingListItemApi,
  type ConsultationRecordingListParams,
  type ConsultationRecordingReprocessApi,
  type ConsultationRecordingSegmentApi,
  type ConsultationRecordingTranscriptApi,
} from "@/lib/consultation-recordings-api";
import { queryKeys } from "@/lib/query-keys";
import { staffAPI } from "@/lib/staff-api";
import type {
  ConsultationRecording,
  ConsultationRecordingListResult,
  ConsultationRecordingProcessingState,
  ConsultationRecordingProviderOption,
  ConsultationRecordingTranscript,
  RecordingSegment,
  RecordingStatus,
  SegmentStatus,
} from "@/components/settings/consultation-recordings-table.types";

const parseOptionalDate = (value: string | null | undefined) =>
  value ? new Date(value) : null;

const mapRecordingStatus = (
  status: ConsultationRecordingListItemApi["status"]
): RecordingStatus => {
  switch (status) {
    case "ready":
      return "Ready";
    case "processing":
      return "Processing";
    case "partial":
      return "Partial";
    case "failed":
      return "Failed";
    default:
      return "Processing";
  }
};

const mapSegmentStatus = (
  status: ConsultationRecordingSegmentApi["status"]
): SegmentStatus => {
  switch (status) {
    case "ready":
      return "Ready";
    case "failed":
      return "Failed";
    default:
      return "Processing";
  }
};

const mapRecording = (
  item: ConsultationRecordingListItemApi
): ConsultationRecording => ({
  id: item.id,
  consultationId: item.consultation_id,
  patient: item.patient_name,
  provider: item.provider_name,
  providerType: item.provider_type,
  status: mapRecordingStatus(item.status),
  start: parseOptionalDate(item.start_time),
  segmentCount: item.segment_count,
  totalDurationMinutes: Math.round(item.total_duration_seconds / 60),
  segments: [],
});

const mapProcessingState = (
  item: ConsultationRecordingReprocessApi
): ConsultationRecordingProcessingState => ({
  id: item.id,
  status: mapRecordingStatus(item.status),
  segmentCount: item.segment_count,
  totalDurationMinutes: Math.round(item.total_duration_seconds / 60),
  stitchedAudioStatus: item.stitched_audio_status,
  stitchedAudioPath: item.stitched_audio_path,
  lastError: item.last_error,
  transcriptStatus: item.transcript_status,
  transcriptDeepgramRequestId: item.transcript_deepgram_request_id,
  finalizationRetryCount: item.finalization_retry_count,
  finalizationLastRetryAt: parseOptionalDate(item.finalization_last_retry_at),
  finalizationLastError: item.finalization_last_error,
  finalizationLastStage: item.finalization_last_stage,
  finalizationLastSignature: item.finalization_last_signature,
});

const mapSegment = (
  segment: ConsultationRecordingSegmentApi
): RecordingSegment => ({
  segmentNumber: segment.segment_number,
  start: parseOptionalDate(segment.start_time),
  end: parseOptionalDate(segment.end_time),
  durationMinutes: Math.round(segment.duration_seconds / 60),
  gapBeforeMinutes:
    segment.gap_before_seconds === null
      ? null
      : Math.round(segment.gap_before_seconds / 60),
  status: mapSegmentStatus(segment.status),
});

const mapTranscript = (
  transcript: ConsultationRecordingTranscriptApi
): ConsultationRecordingTranscript => ({
  id: transcript.id,
  appointmentId: transcript.appointment_id,
  recordingId: transcript.recording_id,
  status: transcript.status,
  finalTranscriptText: transcript.final_transcript_text,
  language: transcript.language,
  durationSeconds: transcript.duration_seconds,
  lastError: transcript.last_error,
  createdAt: parseOptionalDate(transcript.created_at),
  updatedAt: parseOptionalDate(transcript.updated_at),
});

export const useConsultationRecordings = (
  params: ConsultationRecordingListParams
) => {
  return useQuery({
    queryKey: queryKeys.consultationRecordings.list(params),
    queryFn: async (): Promise<ConsultationRecordingListResult> => {
      const response = await consultationRecordingsAPI.list(params);

      return {
        recordings: response.data.map(mapRecording),
        pagination: {
          page: response.pagination.page,
          pageSize: response.pagination.page_size,
          totalCount: response.pagination.total_count,
          totalPages: response.pagination.total_pages,
        },
        stats: {
          totalRecordings: response.stats.total_recordings,
          thisWeekCount: response.stats.this_week_count,
          delta: Math.round(response.stats.this_week_delta_pct),
          averageDurationMinutes: Math.round(
            response.stats.avg_duration_seconds / 60
          ),
          failedCount: response.stats.failed_count,
        },
      };
    },
    retry: false,
  });
};

export const useConsultationRecordingSegments = (
  recordingId: string | null,
  enabled: boolean
) => {
  return useQuery({
    queryKey: queryKeys.consultationRecordings.segments(
      recordingId ?? undefined
    ),
    queryFn: async () => {
      if (!recordingId) {
        return [] as RecordingSegment[];
      }

      const response = await consultationRecordingsAPI.getSegments(recordingId);
      return response.map(mapSegment);
    },
    enabled: enabled && Boolean(recordingId),
    retry: false,
  });
};

export const useConsultationRecordingPlayback = (
  recordingId: string | null,
  enabled: boolean
) => {
  return useQuery({
    queryKey: queryKeys.consultationRecordings.playback(
      recordingId ?? undefined
    ),
    queryFn: async () => {
      if (!recordingId) {
        return null;
      }

      return consultationRecordingsAPI.getPlayback(recordingId);
    },
    enabled: enabled && Boolean(recordingId),
    retry: false,
  });
};

export const useConsultationRecordingTranscript = (
  recordingId: string | null,
  enabled: boolean
) => {
  return useQuery({
    queryKey: queryKeys.consultationRecordings.transcript(
      recordingId ?? undefined
    ),
    queryFn: async () => {
      if (!recordingId) {
        return null;
      }

      const response =
        await consultationRecordingsAPI.getTranscript(recordingId);
      return mapTranscript(response);
    },
    enabled: enabled && Boolean(recordingId),
    retry: false,
  });
};

export const useConsultationRecordingProviders = () => {
  return useQuery({
    queryKey: queryKeys.staff.list({ page: 1, role: "providers", search: "" }),
    queryFn: async (): Promise<ConsultationRecordingProviderOption[]> => {
      const response = await staffAPI.listStaff({ page: 1, page_size: 200 });

      return response.results
        .filter((staff) => staff.is_active)
        .map((staff) => ({
          id: staff.id,
          name: [staff.first_name, staff.last_name].filter(Boolean).join(" "),
        }))
        .sort((left, right) => left.name.localeCompare(right.name));
    },
    retry: false,
  });
};

export const useConsultationRecordingReprocess = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (recordingId: string) => {
      const response = await consultationRecordingsAPI.reprocess(recordingId);
      return mapProcessingState(response);
    },
    onSuccess: async (recording) => {
      queryClient.setQueryData(
        queryKeys.consultationRecordings.detail(recording.id),
        recording
      );
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.consultationRecordings.all,
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.consultationRecordings.playback(recording.id),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.consultationRecordings.transcript(recording.id),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.consultationRecordings.segments(recording.id),
        }),
      ]);
    },
  });
};
